import { InvestmentAccountType, useSubmitAcatDocuments, useUploadAcatDocument, useWealthOverview } from '@egw/api'
import {
  Alert,
  AlertType,
  Button,
  ButtonVariant,
  Input,
  PageHeader,
  Tab,
  TabList,
  TabPanel,
  Tabs,
} from '@egw/components'
import { configClient } from '@egw/config'
import {
  CUSTOMER_SERVICE_PHONE_NUMBER,
  CUSTOMER_SERVICE_PHONE_NUMBER_DISPLAY,
  type FeatureFlags,
  WEALTH_ROUTES,
} from '@egw/constants'
import { FileUpload, type UploadedFile } from '@egw/features-system'
import { Trans, useTranslation } from '@egw/i18n'
import { CheckCircleIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline'
import { yupResolver } from '@hookform/resolvers/yup'
import { useState, type FC } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router'
import * as yup from 'yup'
import { useFlags } from '@egw/feature-flags'
import { SelectDropdownIcon } from '../../components'
import { AccountTransferDrawer } from '../../components/account-transfer-drawer/account-transfer-drawer'
import { AccountTransferField, type TransferListOption, type AccountTransferForm, type Config } from '../../types'

export enum ACCOUNT_TRANSFER_TAB {
  _FULL = 'FULL',
  _PARTIAL = 'PARTIAL',
}

interface SuccessTransferRequestProps {
  accountName: string
  onMakeAnotherTransfer: () => void
}

const SuccessTransferRequest: FC<SuccessTransferRequestProps> = ({ accountName, onMakeAnotherTransfer }) => {
  const { t } = useTranslation('features-transfer')
  return (
    <>
      <CheckCircleIcon className="size-12 text-text-header-success mb-sm stroke-1" />
      <p className="typo-primary-xl-light text-text-header-success mb-md">
        {t('tabbed_account_transfer.full_transfer.transfer_request_received')}
      </p>
      <p className="typo-primary-base text-text-header-success mb-md">
        {t('tabbed_account_transfer.full_transfer.congratulations')}
      </p>

      <p className="typo-primary-base text-text-body mb-xl">
        {t('tabbed_account_transfer.full_transfer.process_text', { accountName })}
      </p>
      <Button
        variant={ButtonVariant.Secondary}
        id="makeAnotherAccountTransfer_secondary_btn"
        testId="makeAnotherAccountTransfer_secondary_btn"
        className="mb-4 md:w-fit"
        onClick={() => {
          onMakeAnotherTransfer()
        }}
      >
        {t('tabbed_account_transfer.full_transfer.make_another_transfer')}
      </Button>
    </>
  )
}

interface SuccessTransferRequestPendingProps {
  accountName: string
  onMakeAnotherTransfer: () => void
  jointOwnerName: string
}

const SuccessTransferRequestPending: FC<SuccessTransferRequestPendingProps> = ({
  jointOwnerName,
  accountName,
  onMakeAnotherTransfer,
}) => {
  const { t } = useTranslation('features-transfer')

  return (
    <>
      <ExclamationCircleIcon className="size-12 text-orange-600 mb-sm stroke-1" />
      <p className="typo-primary-xl-light text-orange-600 mb-sm">Transfer Request Pending</p>
      <p className="typo-primary-base text-text-meta mb-sm">
        {t('tabbed_account_transfer.full_transfer.pending.description_1', { jointOwnerName })}
      </p>
      <p className="typo-primary-base text-text-meta mb-xl">
        {t('tabbed_account_transfer.full_transfer.pending.description_2', {
          accountName: accountName || 'your account',
        })}
      </p>

      <Button
        variant={ButtonVariant.Secondary}
        id="makeAnotherAccountTransfer_secondary_btn"
        testId="makeAnotherAccountTransfer_secondary_btn"
        className="mb-4 md:w-fit"
        onClick={() => {
          onMakeAnotherTransfer()
        }}
      >
        {t('tabbed_account_transfer.full_transfer.make_another_transfer')}
      </Button>
    </>
  )
}

export const AccountTransfer: FC = () => {
  const { t } = useTranslation('features-transfer')
  const config = configClient.getConfig() as Config
  const { flags } = useFlags<FeatureFlags>()

  const navigate = useNavigate()
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false)
  const [accountSelected, setAccountSelected] = useState<TransferListOption | undefined>(undefined)
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([])
  const [jointOwnerName, setJointOwnerName] = useState<string | undefined>(undefined) // Used to display the joint owner name in the success component

  const isJointAcatTransfer =
    accountSelected?.accountType === InvestmentAccountType.COMPANION_JOINT_BROKERAGE_JTWROS ||
    accountSelected?.accountType === InvestmentAccountType.COMPANION_JOINT_BROKERAGE_TIC ||
    accountSelected?.accountType === InvestmentAccountType.COMPANION_JOINT_BROKERAGE_TBTE ||
    accountSelected?.accountType === InvestmentAccountType.COMPANION_JOINT_BROKERAGE_CP

  const { data } = useWealthOverview()
  const { mutate } = useUploadAcatDocument()

  const allAccounts = data?.personalPortfolio?.accounts

  // These are the account types that are eligible for ACAT transfers
  // Additionally we check for the acatAllowed flag on the attributes of the account to ensure it is allowed for ACAT transfers
  const coastalAllowedAccounts = allAccounts
    ? allAccounts
        .filter(
          (account) =>
            account.__typename === 'InvestmentBrokerageCompanionAccount' ||
            account.__typename === 'InvestmentIRAAccount' ||
            account.__typename === 'InvestmentRothIRAAccount' ||
            account.__typename === 'InvestmentInheritedIRAAccount' ||
            account.__typename === 'InvestmentInheritedRothIRAAccount' ||
            account.__typename === 'InvestmentJointBrokerageTenantsInCommonCompanionAccount' ||
            account.__typename === 'InvestmentJointBrokerageTenantsWithRightOfSurvivorshipCompanionAccount' ||
            account.__typename === 'InvestmentJointBrokerageTenantsByEntiretyCompanionAccount' ||
            account.__typename === 'InvestmentJointBrokerageCommunityPropertyCompanionAccount',
        )
        .filter((e) => e.attributes?.acatAllowed)
    : []

  const apexAllowedAccounts = allAccounts
    ? allAccounts
        .filter(
          (account) =>
            account.__typename === 'InvestmentBrokerageAccount' ||
            account.__typename === 'InvestmentRothIRAAccount' ||
            account.__typename === 'InvestmentIRAAccount' ||
            account.__typename === 'InvestmentInheritedIRAAccount' ||
            account.__typename === 'InvestmentInheritedRothIRAAccount' ||
            account.__typename === 'InvestmentJointBrokerageTenantsInCommonAccount' ||
            account.__typename === 'InvestmentJointBrokerageTenantsWithRightOfSurvivorshipAccount' ||
            account.__typename === 'InvestmentJointBrokerageTenantsByEntiretyAccount' ||
            account.__typename === 'InvestmentJointBrokerageCommunityPropertyAccount',
        )
        .filter((e) => e.attributes.acatAllowed)
    : []

  const openToDrawer = (): void => {
    setIsDrawerOpen(true)
  }

  const formMethods = useForm<AccountTransferForm>({
    mode: 'all',
    defaultValues: {
      [AccountTransferField.To]: '',
    },
    resolver: yupResolver(
      yup.object().shape({
        to: yup.string().required(),
      }),
    ),
  })

  const {
    mutate: submitAcatDocuments,
    isLoading,
    isSuccess,
    error,
  } = useSubmitAcatDocuments({
    onSuccess: (res) => {
      if (res.__typename === 'SubmitAcatDocumentsSuccess' && res.isSuccessful) {
        if (isJointAcatTransfer && res.pendingJointAcatTransfer?.jointAccountHolderFirstName) {
          setJointOwnerName(res.pendingJointAcatTransfer.jointAccountHolderFirstName)
        }
      }
    },
  })

  const onSubmit = formMethods.handleSubmit(async (_formData: AccountTransferForm): Promise<void> => {
    if (accountSelected?.alias !== undefined) {
      await submitAcatDocuments({
        accountAlias: accountSelected.alias,
        filePaths: uploadedFiles.map((file) => file.filePath),
        disclosures: [{ id: config.acat.disclosureId, accepted: true }],
      })
    }
  })

  return (
    <>
      <PageHeader
        backLinkUrl={WEALTH_ROUTES.TRANSFER_ACCOUNT.backPath}
        subtitle={t('transfer.title')}
        title={t('tabbed_account_transfer.title')}
      />
      <div className="egw-container">
        {isSuccess ? (
          <>
            {isJointAcatTransfer ? (
              <SuccessTransferRequestPending
                jointOwnerName={jointOwnerName ?? ''}
                accountName={accountSelected.display.name ?? ''}
                onMakeAnotherTransfer={() => {
                  formMethods.reset()
                  void navigate(WEALTH_ROUTES.TRANSFER.path)
                }}
              />
            ) : null}
            {!isJointAcatTransfer && (
              <SuccessTransferRequest
                accountName={accountSelected?.display.name ?? ''}
                onMakeAnotherTransfer={() => {
                  formMethods.reset()
                  void navigate(WEALTH_ROUTES.TRANSFER.path)
                }}
              />
            )}
          </>
        ) : (
          <>
            <p className="typo-primary-base text-tex-meta mb-2xl">{t('tabbed_account_transfer.description')}</p>
            <Tabs defaultSelectedTab={ACCOUNT_TRANSFER_TAB._FULL}>
              <TabList>
                <Tab id={ACCOUNT_TRANSFER_TAB._FULL} label={t('tabbed_account_transfer.full_transfer.tab_title')} />
                <Tab
                  id={ACCOUNT_TRANSFER_TAB._PARTIAL}
                  label={t('tabbed_account_transfer.partial_transfer.tab_title')}
                />
              </TabList>
              <TabPanel id={ACCOUNT_TRANSFER_TAB._FULL}>
                <div className="mt-lg">
                  <FormProvider {...formMethods}>
                    <form
                      className="flex w-full flex-col"
                      id="account-transfer-form"
                      onSubmit={(e) => void onSubmit(e)}
                    >
                      <Input
                        readOnly
                        autoComplete="off"
                        id={AccountTransferField.To}
                        onFocus={openToDrawer}
                        label={t('tabbed_account_transfer.full_transfer.to')}
                        icon={<SelectDropdownIcon onClick={openToDrawer} isDisabled={false} />}
                        value={formMethods.getValues(AccountTransferField.To)}
                        disabled={isLoading}
                      />

                      <Controller
                        name={AccountTransferField.To}
                        control={formMethods.control}
                        render={({ field }) => <input type="hidden" {...field} value={field.value} />}
                      />

                      <div className="mt-lg">
                        <FileUpload
                          onChange={(files) => {
                            setUploadedFiles(files)
                          }}
                          mutate={mutate}
                          testId="account-transfer-file-upload"
                          listTitle={t('tabbed_account_transfer.upload_your_statements')}
                          buttonLabel={t('tabbed_account_transfer.upload_statement')}
                          isDisabled={isLoading}
                        />
                      </div>

                      <p className="typo-primary-base text-text-meta my-2xl">
                        <Trans t={t} i18nKey="tabbed_account_transfer.call_text">
                          <a
                            href={`tel:${CUSTOMER_SERVICE_PHONE_NUMBER}`}
                            title={t('tabbed_account_transfer.call')}
                            rel="noopener"
                            className="text-text-action hover:underline hover:cursor-pointer typo-primary-base-semibold"
                          >
                            {CUSTOMER_SERVICE_PHONE_NUMBER_DISPLAY}
                          </a>
                        </Trans>
                      </p>

                      <div className="typo-primary-sm text-text-meta mb-sm">
                        <Trans t={t} i18nKey="tabbed_account_transfer.full_transfer.submit_transfer_terms">
                          <a
                            target="_blank"
                            href={config.zendeskArticles.acatTermsAndConditions}
                            className="typo-primary-sm-semibold inline cursor-pointer hover:underline"
                            rel="noopener"
                          >
                            {t('tabbed_account_transfer.full_transfer.acat_terms_and_conditions')}
                          </a>
                        </Trans>
                      </div>

                      {error ? (
                        <div className="mb-md">
                          <Alert description={t('tabbed_account_transfer.acat_error')} alertType={AlertType.Error} />
                        </div>
                      ) : null}

                      <Button
                        type="submit"
                        variant={ButtonVariant.Primary}
                        id="submitTransfer_primary_btn"
                        testId="submitTransfer_primary_btn"
                        className="mb-4 md:w-fit"
                        isLoading={isLoading}
                        isDisabled={
                          !formMethods.getValues(AccountTransferField.To) ||
                          accountSelected?.alias === undefined ||
                          uploadedFiles.length === 0
                        }
                      >
                        {t('tabbed_account_transfer.full_transfer.submit_transfer_request')}
                      </Button>
                    </form>
                  </FormProvider>
                </div>
              </TabPanel>
              <TabPanel id={ACCOUNT_TRANSFER_TAB._PARTIAL}>
                <p className="typo-primary-sm-semibold text-tex-body mt-lg mb-2xl">
                  {t('tabbed_account_transfer.partial_transfer.text')}
                </p>
                <p className="typo-primary-base text-text-meta">
                  <Trans t={t} i18nKey="tabbed_account_transfer.call_text">
                    <a
                      href={`tel:${CUSTOMER_SERVICE_PHONE_NUMBER}`}
                      title={t('tabbed_account_transfer.call')}
                      rel="noopener"
                      className="text-text-action hover:underline hover:cursor-pointer typo-primary-base-semibold"
                    >
                      {CUSTOMER_SERVICE_PHONE_NUMBER_DISPLAY}
                    </a>
                  </Trans>
                </p>
              </TabPanel>
            </Tabs>
          </>
        )}
      </div>
      <AccountTransferDrawer
        isModalOpen={isDrawerOpen}
        onClose={() => {
          setIsDrawerOpen(false)
        }}
        options={apexAllowedAccounts}
        onSelect={(option) => {
          formMethods.setValue(AccountTransferField.To, option.display.name)
          setAccountSelected(option)
          setIsDrawerOpen(false)
        }}
      />
    </>
  )
}
